import React from "react";
import {
  FaAlignCenter,
  FaAlignLeft,
  FaAlignRight,
  FaAngleDoubleDown,
  FaAngleDoubleUp,
  FaAngleDown,
  FaAngleUp,
  FaTrash,
} from "react-icons/fa";

import { FONTS, NOTE_STYLES, SWATCHES } from "./constants";

const HAS_FILL = ["rect", "ellipse"];
const HAS_STROKE = ["rect", "ellipse", "line", "image"];
const HAS_RADIUS = ["rect", "image"];
const HAS_TEXT = ["text"];

function Section({ title, children }) {
  return (
    <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(148,163,184,0.25)" }}>
      <div
        className="fw-bold mb-2"
        style={{
          fontSize: "0.68rem",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#94a3b8",
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Num({ label, value, onChange, onBeforeChange, step = 1, min, max }) {
  return (
    <label style={{ flex: "1 1 60px", fontSize: "0.7rem", color: "#94a3b8" }}>
      {label}
      <input
        type="number"
        value={Math.round(value)}
        step={step}
        min={min}
        max={max}
        // One history entry per editing session, not per keystroke.
        onFocus={onBeforeChange}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          display: "block",
          width: "100%",
          marginTop: "2px",
          padding: "5px 7px",
          borderRadius: "7px",
          border: "1px solid rgba(148,163,184,0.4)",
          background: "rgba(255,255,255,0.06)",
          color: "inherit",
          fontSize: "0.8rem",
          outline: "none",
        }}
      />
    </label>
  );
}

function Swatches({ value, onPick, colors = SWATCHES }) {
  return (
    <div className="d-flex flex-wrap gap-1">
      {colors.map((c) => (
        <button
          key={c}
          onClick={() => onPick(c)}
          title={c}
          style={{
            width: "22px",
            height: "22px",
            borderRadius: "6px",
            cursor: "pointer",
            border: value === c ? "2px solid #ff6b35" : "1px solid rgba(148,163,184,0.5)",
            background:
              c === "transparent"
                ? "repeating-conic-gradient(#cbd5e1 0 25%, #fff 0 50%) 50%/8px 8px"
                : c,
          }}
        />
      ))}
    </div>
  );
}

function IconBtn({ active, title, onClick, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        flex: 1,
        padding: "6px",
        borderRadius: "7px",
        cursor: "pointer",
        border: "1px solid rgba(148,163,184,0.4)",
        background: active ? "#ff6b35" : "rgba(255,255,255,0.06)",
        color: active ? "#fff" : "inherit",
      }}
    >
      {children}
    </button>
  );
}

function Inspector({ el, onPatch, onBeforeChange, onDelete, onLayer }) {
  if (!el) {
    return (
      <div
        style={{
          padding: "22px 16px",
          color: "#94a3b8",
          fontSize: "0.85rem",
          lineHeight: 1.7,
        }}
      >
        Select an element on the canvas to edit it.
        <div style={{ marginTop: "12px", fontSize: "0.78rem" }}>
          <div>
            <kbd>Ctrl</kbd>+<kbd>S</kbd> save · <kbd>Ctrl</kbd>+<kbd>Z</kbd> undo ·{" "}
            <kbd>Del</kbd> remove
          </div>
          <div>
            <kbd>Shift</kbd> + drag — lock axis / keep ratio
          </div>
          <div>Arrow keys nudge · double-click text to edit</div>
        </div>
      </div>
    );
  }

  const set = (patch) => onPatch(el.id, patch);
  // Discrete choices (a swatch, an alignment) are their own undo step.
  const setNow = (patch) => {
    onBeforeChange();
    set(patch);
  };

  return (
    <div>
      <Section title={`${el.type} element`}>
        <div className="d-flex gap-2 mb-2">
          <Num label="X" value={el.x} onChange={(x) => set({ x })} onBeforeChange={onBeforeChange} />
          <Num label="Y" value={el.y} onChange={(y) => set({ y })} onBeforeChange={onBeforeChange} />
        </div>
        <div className="d-flex gap-2 mb-2">
          <Num label="W" value={el.w} onChange={(w) => set({ w })} onBeforeChange={onBeforeChange} min={4} />
          <Num
            label="H"
            value={el.h}
            onChange={(h) => set({ h })}
            onBeforeChange={onBeforeChange}
            min={el.type === "line" ? 0 : 4}
          />
        </div>
        <div className="d-flex gap-2">
          <Num
            label="Rotation°"
            value={el.rotation}
            onChange={(rotation) => set({ rotation })}
            onBeforeChange={onBeforeChange}
          />
          <Num
            label="Opacity %"
            value={el.opacity * 100}
            min={0}
            max={100}
            step={5}
            onChange={(v) => set({ opacity: Math.min(1, Math.max(0, v / 100)) })}
            onBeforeChange={onBeforeChange}
          />
        </div>
      </Section>

      {el.type === "note" && (
        <Section title="Paper colour">
          <Swatches
            value={NOTE_STYLES[el.paletteIndex]?.paper}
            colors={NOTE_STYLES.map((s) => s.paper)}
            onPick={(paper) =>
              setNow({ paletteIndex: NOTE_STYLES.findIndex((s) => s.paper === paper) })
            }
          />
          <div className="d-flex gap-2 mt-2">
            <Num
              label="Text size"
              value={el.fontSize}
              min={8}
              max={80}
              onChange={(fontSize) => set({ fontSize })}
              onBeforeChange={onBeforeChange}
            />
          </div>
        </Section>
      )}

      {HAS_FILL.includes(el.type) && (
        <Section title="Fill">
          <Swatches value={el.fill} onPick={(fill) => setNow({ fill })} />
        </Section>
      )}

      {HAS_TEXT.includes(el.type) && (
        <Section title="Text">
          <select
            value={el.fontFamily}
            onChange={(e) => setNow({ fontFamily: e.target.value })}
            style={{
              width: "100%",
              padding: "6px 8px",
              borderRadius: "7px",
              border: "1px solid rgba(148,163,184,0.4)",
              background: "rgba(255,255,255,0.06)",
              color: "inherit",
              marginBottom: "8px",
            }}
          >
            {FONTS.map((f) => (
              <option key={f.label} value={f.value} style={{ color: "#0f172a" }}>
                {f.label}
              </option>
            ))}
          </select>

          <div className="d-flex gap-2 mb-2">
            <Num
              label="Size"
              value={el.fontSize}
              min={4}
              max={400}
              onChange={(fontSize) => set({ fontSize })}
              onBeforeChange={onBeforeChange}
            />
            <label style={{ flex: "1 1 60px", fontSize: "0.7rem", color: "#94a3b8" }}>
              Weight
              <select
                value={el.fontWeight}
                onChange={(e) => setNow({ fontWeight: e.target.value })}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: "2px",
                  padding: "5px 7px",
                  borderRadius: "7px",
                  border: "1px solid rgba(148,163,184,0.4)",
                  background: "rgba(255,255,255,0.06)",
                  color: "inherit",
                }}
              >
                {["300", "400", "600", "700", "900"].map((w) => (
                  <option key={w} value={w} style={{ color: "#0f172a" }}>
                    {w}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="d-flex gap-1 mb-2">
            <IconBtn
              active={el.align === "left"}
              title="Align left"
              onClick={() => setNow({ align: "left" })}
            >
              <FaAlignLeft />
            </IconBtn>
            <IconBtn
              active={el.align === "center"}
              title="Align centre"
              onClick={() => setNow({ align: "center" })}
            >
              <FaAlignCenter />
            </IconBtn>
            <IconBtn
              active={el.align === "right"}
              title="Align right"
              onClick={() => setNow({ align: "right" })}
            >
              <FaAlignRight />
            </IconBtn>
          </div>

          <Swatches value={el.color} onPick={(color) => setNow({ color })} />
        </Section>
      )}

      {HAS_STROKE.includes(el.type) && (
        <Section title={el.type === "line" ? "Line" : "Border"}>
          <Swatches value={el.stroke} onPick={(stroke) => setNow({ stroke })} />
          <div className="d-flex gap-2 mt-2">
            <Num
              label="Width"
              value={el.strokeWidth}
              min={0}
              max={60}
              onChange={(strokeWidth) => set({ strokeWidth })}
              onBeforeChange={onBeforeChange}
            />
            {HAS_RADIUS.includes(el.type) && (
              <Num
                label="Radius"
                value={el.radius}
                min={0}
                max={400}
                onChange={(radius) => set({ radius })}
                onBeforeChange={onBeforeChange}
              />
            )}
          </div>
        </Section>
      )}

      <Section title="Arrange">
        <div className="d-flex gap-1">
          <IconBtn title="Bring to front" onClick={() => onLayer(el.id, "front")}>
            <FaAngleDoubleUp />
          </IconBtn>
          <IconBtn title="Bring forward" onClick={() => onLayer(el.id, "up")}>
            <FaAngleUp />
          </IconBtn>
          <IconBtn title="Send backward" onClick={() => onLayer(el.id, "down")}>
            <FaAngleDown />
          </IconBtn>
          <IconBtn title="Send to back" onClick={() => onLayer(el.id, "back")}>
            <FaAngleDoubleDown />
          </IconBtn>
        </div>
      </Section>

      <div style={{ padding: "12px 14px" }}>
        <button
          onClick={() => onDelete(el.id)}
          className="fw-bold w-100"
          style={{
            border: "none",
            borderRadius: "9px",
            padding: "9px",
            background: "rgba(220,38,38,0.15)",
            color: "#f87171",
            cursor: "pointer",
          }}
        >
          <FaTrash className="me-2" />
          Delete element
        </button>
      </div>
    </div>
  );
}

export default Inspector;
